import { supabase } from '../lib/supabase';

const toSnakeCase = (settings) => ({
  is_enabled: settings?.isEnabled,
  custom_domain: settings?.customDomain,
  cname_verified: settings?.cnameVerified,
  logo_url: settings?.logoUrl,
  favicon_url: settings?.faviconUrl,
  primary_color: settings?.primaryColor,
  email_footer_text: settings?.emailFooterText,
  agency_name: settings?.agencyName,
  // Only include customer_config when explicitly provided so callers that
  // save branding-only settings don't wipe the stored customer config.
  ...(settings?.customerConfig !== undefined ? { customer_config: settings.customerConfig } : {}),
});

const toCamelCase = (row) => ({
  id: row?.id,
  userId: row?.user_id,
  isEnabled: row?.is_enabled,
  customDomain: row?.custom_domain,
  cnameVerified: row?.cname_verified,
  cnameVerifiedAt: row?.cname_verified_at,
  logoUrl: row?.logo_url,
  faviconUrl: row?.favicon_url,
  primaryColor: row?.primary_color,
  emailFooterText: row?.email_footer_text,
  agencyName: row?.agency_name,
  customerConfig: row?.customer_config ?? {},
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
});

export const whiteLabelService = {
  async getSettings() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('white_label_settings')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();

    if (error) {
      if (error?.code?.startsWith('42')) throw error;
      return null;
    }
    return data ? toCamelCase(data) : null;
  },

  async saveSettings(settings) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const payload = {
      ...toSnakeCase(settings),
      user_id: user?.id,
    };

    const { data, error } = await supabase?.from('white_label_settings')?.upsert(payload, { onConflict: 'user_id' })?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async uploadAsset(file, type) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const ext = file?.name?.split('.')?.pop();
    const filePath = `${user?.id}/${type}-${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase?.storage?.from('white-label-assets')?.upload(filePath, file, { upsert: true, cacheControl: '3600' });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase?.storage?.from('white-label-assets')?.getPublicUrl(filePath);

    return publicUrl;
  },

  async verifyCname(domain) {
    // Simulate CNAME verification — in production this would call an edge function
    // For now, mark as pending verification
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('white_label_settings')?.update({ custom_domain: domain, cname_verified: false })?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },
};
